import TextSkeleton from "components/shared/TextSkeleton";
import { isBookPublisher } from "helpers/publisher";
import { useCompanySettings } from "hooks/useCompanySettings";

const MAX_SKELETON_SLICES = 3;
const PREVIEW_COLUMNS = ["title", "author", "balance", "advance", "gross", "next", "action"] as const;

type NewAdvanceModalSkeletonProps = {
    sliceCount: number;
    showManagementPreview: boolean;
};

/**
 * Placeholder laid out like the loaded advance modal, so the dialog keeps its size while data loads.
 */
const NewAdvanceModalSkeleton = ({ sliceCount, showManagementPreview }: NewAdvanceModalSkeletonProps) => {
    const { companyInfos } = useCompanySettings();
    const showCustomStatus = isBookPublisher(companyInfos?.publisherType);
    const rowCount = Math.min(Math.max(sliceCount, 1), MAX_SKELETON_SLICES);

    return (
        <div className="advance-modal-skeleton" aria-hidden>
            <TextSkeleton variant="rounded" width="100%" height={40} />
            <div className="advance-modal-skeleton__slices">
                <div
                    className={`advance-modal-skeleton__table advance-modal-skeleton__slices-table${
                        showCustomStatus ? " advance-modal-skeleton__slices-table--with-status" : ""
                    }`}
                >
                    <div className="advance-modal-skeleton__table-row advance-modal-skeleton__table-row--head">
                        <TextSkeleton width="70%" height={12} />
                        <TextSkeleton width="70%" height={12} />
                        <TextSkeleton width="70%" height={12} />
                        <TextSkeleton width="70%" height={12} />
                        {showCustomStatus && <TextSkeleton width="70%" height={12} />}
                        <TextSkeleton width="70%" height={12} />
                    </div>
                    {Array.from({ length: rowCount }, (_, index) => (
                        <div className="advance-modal-skeleton__table-row" key={`advance-skeleton-slice-${index}`}>
                            <TextSkeleton variant="rounded" width="100%" height={18} />
                            <TextSkeleton variant="rounded" width="100%" height={18} />
                            <TextSkeleton variant="rounded" width="100%" height={18} />
                            <TextSkeleton variant="rounded" width="100%" height={18} />
                            {showCustomStatus && <TextSkeleton variant="rounded" width="100%" height={18} />}
                            <TextSkeleton variant="rounded" width="100%" height={18} />
                        </div>
                    ))}
                </div>
                <div className="advance-modal-skeleton__footer">
                    <TextSkeleton width={200} height={14} />
                    <TextSkeleton width={150} height={14} />
                </div>
            </div>
            {showManagementPreview && (
                <div className="advance-modal-skeleton__preview">
                    <TextSkeleton width={260} height={16} />
                    <div className="advance-modal-skeleton__table">
                        <div className="advance-modal-skeleton__table-row advance-modal-skeleton__table-row--head">
                            {PREVIEW_COLUMNS.map((column) => (
                                <TextSkeleton key={`advance-skeleton-head-${column}`} width="70%" height={12} />
                            ))}
                        </div>
                        <div className="advance-modal-skeleton__table-row">
                            {PREVIEW_COLUMNS.map((column) => (
                                <TextSkeleton
                                    key={`advance-skeleton-cell-${column}`}
                                    variant="rounded"
                                    width="100%"
                                    height={column === "action" ? 18 : 28}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <TextSkeleton variant="rounded" width="100%" height={32} />
        </div>
    );
};

export default NewAdvanceModalSkeleton;
